export interface IDatePicker {
  /**
   * The current value of the date picker, represented as a string.
   * Example: "2024-12-25"
   */
  value?: string;

  /**
   * Optional custom CSS class for styling the date picker container.
   */
  className?: string;

  /**
   * Placeholder text to display when no date is selected.
   */
  placeholder?: string;

  /**
   * Label text to display above the date picker.
   */
  label?: string;

  /**
   * Marks the field as required. When true, indicates that the date picker must have a value.
   */
  required?: boolean;

  /**
   * Disables the date picker when set to true, preventing user interaction.
   */
  disabled?: boolean;

  /**
   * The format in which the date should be displayed.
   * Example: "MM/DD/YYYY", "DD/MM/YYYY", "YYYY/MM/DD" etc.
   */
  dateFormat?: string;

  /**
   * The earliest selectable date.
   */
  minDate?: Date;

  /**
   * The latest selectable date.
   */
  maxDate?: Date;

  /**
   * Indicates if the date value has changed.
   */
  hasChanged?: boolean;

  /**
   * Custom style variant to apply when the date value has changed.
   * Options are defined in the `DatePickerChangeVariant` enum.
   */
  datePickerChangedStyle?: DatePickerChangeVariant;

  /**
   * Highlights the date picker visually when the value changes, if set to true.
   */
  highlightOnChange?: boolean;

  /**
   * Callback function triggered when the date value changes.
   * @param value - The selected date as a `Date` object or `null` if cleared.
   */
  onChange?: (value: Date | null) => void;

  /**
   * Optional override for calendar dropdown position. 'above' will render the panel above the input,
   * 'below' will render below. If undefined, default auto placement is used.
   */
  panelPosition?: 'above' | 'below';
}

export enum DatePickerChangeVariant {
  Default = '',
  Secondary = 'secondary',
}

export enum DateFormat {
  MM_DD_YYYY = 'MM/DD/YYYY',
  DD_MM_YYYY = 'DD/MM/YYYY',
  YYYY_MM_DD = 'YYYY/MM/DD',
}

export const DateFormatMapping: Record<DateFormat, { display: string; focus: string }> = {
  [DateFormat.MM_DD_YYYY]: { display: 'MM dd, yy', focus: 'mm/dd/yy' },
  [DateFormat.DD_MM_YYYY]: { display: 'dd MM yy', focus: 'dd/mm/yy' },
  [DateFormat.YYYY_MM_DD]: { display: 'yy MM dd', focus: 'yy/mm/dd' },
};
